import { useState } from 'react'
import { Dialog, DialogPanel } from '@headlessui/react'
import {
  CalendarIcon,
  ClockIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'
import { useNavigate } from 'react-router-dom'
import { toast } from 'sonner'

import { CalendarEvent, RepeatingEvent } from '../../store/slices/slices.types'
import { deleteRecurringEvent } from '../../lib/db'
import { getRepeatDescription } from './RepeatDescription'

interface RepeatingEventDetailProps {
  workspaceId: string
  repeatingEvent: RepeatingEvent
  calendarEvents: CalendarEvent[]
}

export function RepeatingEventDetail({
  workspaceId,
  repeatingEvent,
  calendarEvents,
}: RepeatingEventDetailProps) {
  const navigate = useNavigate()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)

  const handleDelete = async () => {
    try {
      await deleteRecurringEvent({
        workspaceId,
        eventId: repeatingEvent.id,
      })
      toast.success('Recurring event deleted')
      navigate(`/workspace/${workspaceId}/manage-events/`)
    } catch (error) {
      console.error('Failed to delete event:', error)
    }
  }

  const sortedEvents = [...calendarEvents].sort(
    (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
  )

  return (
    <div className="space-y-6">
      {/* Recurrence Schedule Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-semibold text-gray-900">
            {repeatingEvent.name}
          </h2>
          {!repeatingEvent.isSystemEvent && (
            <button
              onClick={() => setShowDeleteDialog(true)}
              className="text-red-600 hover:text-red-700 p-2 rounded-full hover:bg-red-50"
              title="Delete recurring event"
            >
              <TrashIcon className="h-5 w-5" />
            </button>
          )}
        </div>
        <div className="space-y-3">
          <div className="flex items-start gap-2 text-gray-600">
            <CalendarIcon className="h-5 w-5 mt-0.5" />
            <div>
              <p className="font-medium">Recurrence Schedule</p>
              <p className="text-sm text-gray-500">
                {getRepeatDescription(repeatingEvent.repeats)}
              </p>
              {repeatingEvent.repeats.end && (
                <p className="text-sm text-gray-500">
                  Until{' '}
                  {new Date(repeatingEvent.repeats.end).toLocaleDateString()}
                </p>
              )}
            </div>
          </div>
          {repeatingEvent.repeats.exceptions &&
            repeatingEvent.repeats.exceptions.length > 0 && (
              <div className="flex items-start gap-2 text-gray-600">
                <ClockIcon className="h-5 w-5 mt-0.5" />
                <div>
                  <p className="font-medium">Exceptions</p>
                  <p className="text-sm text-gray-500">
                    {repeatingEvent.repeats.exceptions.length} date
                    {repeatingEvent.repeats.exceptions.length === 1
                      ? ''
                      : 's'}{' '}
                    excluded
                  </p>
                </div>
              </div>
            )}
        </div>
      </div>

      {/* Calendar Events Section */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">
            Calendar Events ({calendarEvents.length})
          </h3>
        </div>
        <ul className="divide-y divide-gray-200">
          {sortedEvents.map((event) => (
            <li key={event.id} className="px-6 py-4">
              <div className="flex justify-between items-center">
                <div>
                  <p className="font-medium text-gray-900">
                    {new Date(event.date).toLocaleDateString()}
                  </p>
                  {event.notes && (
                    <p className="text-sm text-gray-500 mt-1">{event.notes}</p>
                  )}
                </div>
                {event.isCompleted && (
                  <div className="text-sm text-green-500">Completed</div>
                )}
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={showDeleteDialog}
        onClose={() => setShowDeleteDialog(false)}
        className="relative z-50"
      >
        <div className="fixed inset-0 bg-black/30" aria-hidden="true" />

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <DialogPanel className="w-full max-w-md rounded-lg bg-white p-4 shadow-xl">
            <div className="flex flex-col gap-4">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-red-100">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-600" />
                </div>
                <Dialog.Title className="text-lg font-medium text-gray-900">
                  Delete Recurring Event
                </Dialog.Title>
                <button
                  onClick={() => setShowDeleteDialog(false)}
                  className="absolute right-4 top-4 text-gray-400 hover:text-gray-500"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              <div className="text-sm text-gray-500">
                <p>Are you sure you want to delete this recurring event?</p>
                <p className="mt-2">
                  Only future calendar events will be removed - past events will
                  remain in your calendar.
                </p>
              </div>

              <div className="flex justify-end gap-3">
                <button
                  type="button"
                  onClick={() => setShowDeleteDialog(false)}
                  className="rounded-md px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="rounded-md bg-red-600 px-3 py-2 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                >
                  Delete
                </button>
              </div>
            </div>
          </DialogPanel>
        </div>
      </Dialog>
    </div>
  )
}
